/**
 * STOQ Restock Modal — Variant Filter
 *
 * STOQ renders its "Notify me" dropdown (<select id="form-Select"> inside the
 * #restock-rocket modal, one <option value="{variant_id}"> per variant) and then fires
 * `stoq:restock-modal:opened` on window with
 * `event.detail = { action, product, variant }`. There is no pre-filter hook,
 * so this module prunes the dropdown right after it opens.
 *
 * Which variants survive is decided server-side in
 * snippets/stoq-variant-filter.liquid (variant metafields are not exposed to
 * STOQ's payload). This file only:
 *   1. removes options whose variant ID is not in the allowed set,
 *   2. strips the "-U" suffix from the remaining labels,
 *   3. re-selects a valid option (and notifies STOQ via `input`/`change`) when
 *      the pre-selected variant was removed, re-checking after STOQ's render
 *      settles so its own state cannot leave the select blank.
 */

const CONFIG_SELECTOR = "[data-stoq-variant-filter]";
const SELECT_SELECTOR = "#restock-rocket #form-Select";
const DISTRIBUTOR_SUFFIX = /\s*[-–]\s*U\s*$/i;

/**
 * @typedef {Object} FilterConfig
 * @property {string}      productId
 * @property {Set<string>} keepVariantIds
 */

/**
 * Reads and validates the JSON emitted by stoq-variant-filter.liquid.
 * @returns {FilterConfig | null}
 */
function readConfig() {
  const el = document.querySelector(CONFIG_SELECTOR);
  if (!el) return null;

  try {
    const parsed = JSON.parse(el.textContent ?? "");
    if (!parsed || !Array.isArray(parsed.keepVariantIds)) return null;

    return {
      productId: String(parsed.productId),
      keepVariantIds: new Set(parsed.keepVariantIds.map(String)),
    };
  } catch (error) {
    console.warn("[stoq-variant-filter] Invalid config JSON", error);
    return null;
  }
}

/**
 * Removes options outside the allowed set and cleans the remaining labels.
 * Idempotent, so it is safe to run more than once per open.
 *
 * @param {HTMLSelectElement} select
 * @param {Set<string>}       keepVariantIds
 * @returns {boolean} false when nothing matched and the list was left intact
 */
function filterSelect(select, keepVariantIds) {
  const options = Array.from(select.options).filter(option => option.value !== "");

  // Never empty the dropdown: if none of STOQ's options match the allowed set
  // (unexpected ID format, stale config), leave the list intact.
  const matching = options.filter(option => keepVariantIds.has(option.value));
  if (matching.length === 0) {
    cleanLabels(options);
    return false;
  }

  for (const option of options) {
    if (!keepVariantIds.has(option.value)) option.remove();
  }

  cleanLabels(matching);
  return true;
}

/**
 * Makes sure an allowed option is selected and tells STOQ about it.
 *
 * STOQ keeps its own "selected variant" state and re-applies it to the
 * <select> after `stoq:restock-modal:opened` fires. When that variant was
 * removed, the select ends up with no matching option and renders blank, so
 * we pick the first allowed option and dispatch `input` + `change` for STOQ's
 * handlers to adopt it.
 *
 * @param {HTMLSelectElement} select
 * @param {Set<string>}       keepVariantIds
 */
function ensureValidSelection(select, keepVariantIds) {
  const current = select.selectedOptions[0];
  if (current && keepVariantIds.has(current.value)) return;

  const fallback = Array.from(select.options).find(option => keepVariantIds.has(option.value));
  if (!fallback) return;

  select.value = fallback.value;
  select.dispatchEvent(new Event("input", { bubbles: true }));
  select.dispatchEvent(new Event("change", { bubbles: true }));
}

/**
 * @param {Set<string>} keepVariantIds
 */
function applyFilter(keepVariantIds) {
  const select = document.querySelector(SELECT_SELECTOR);
  if (!(select instanceof HTMLSelectElement)) return; // single-variant product — no dropdown

  if (!filterSelect(select, keepVariantIds)) return;
  ensureValidSelection(select, keepVariantIds);
}

/** @param {HTMLOptionElement[]} options */
function cleanLabels(options) {
  for (const option of options) {
    const label = option.textContent ?? "";
    const cleaned = label.replace(DISTRIBUTOR_SUFFIX, "");
    if (cleaned !== label) option.textContent = cleaned;
  }
}

window.addEventListener("stoq:restock-modal:opened", event => {
  const config = readConfig();
  if (!config) return;

  const detail = /** @type {CustomEvent} */ (event).detail ?? {};

  // The config describes the page's product; ignore modals opened for another one.
  const eventProductId = detail.product?.id;
  if (eventProductId != null && String(eventProductId) !== config.productId) return;

  // Prune immediately so disallowed options are never visible, then re-run
  // once STOQ has finished committing its render (it re-applies its selected
  // variant after this event) so the fallback selection sticks.
  applyFilter(config.keepVariantIds);
  requestAnimationFrame(() => {
    applyFilter(config.keepVariantIds);
    requestAnimationFrame(() => applyFilter(config.keepVariantIds));
  });
});
